import React, { useState, useEffect } from "react";
import { useAuth } from "../App";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import {
  ShieldCheck, AlertTriangle, AlertCircle, Info, RefreshCw,
  ChevronDown, ChevronRight, Clock, Database, Users, FileText,
  Activity, Building2, CheckCircle2, XCircle, ExternalLink
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import Layout from "../components/Layout";

import { API } from "../config/api";

const SEVERITY_CONFIG = {
  high: { color: "bg-red-50 border-red-200 text-red-700", icon: XCircle, iconColor: "text-red-500", badge: "bg-red-100 text-red-700" },
  medium: { color: "bg-amber-50 border-amber-200 text-amber-700", icon: AlertTriangle, iconColor: "text-amber-500", badge: "bg-amber-100 text-amber-700" },
  low: { color: "bg-blue-50 border-blue-200 text-blue-700", icon: Info, iconColor: "text-blue-500", badge: "bg-blue-100 text-blue-700" },
};

export default function DataHealthPage() {
  const { user } = useAuth();
  const [audit, setAudit] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [expandedItems, setExpandedItems] = useState({});

  useEffect(() => { fetchLatest(); }, []);

  const fetchLatest = async () => {
    setLoading(true);
    try {
      const [latestRes, histRes] = await Promise.all([
        axios.get(`${API}/data-health/latest`),
        axios.get(`${API}/data-health/history?limit=10`),
      ]);
      setAudit(latestRes.data);
      setHistory(histRes.data);
    } catch (e) {
      console.error("Failed to fetch audit:", e);
    } finally {
      setLoading(false);
    }
  };

  const runAudit = async () => {
    setRunning(true);
    try {
      const res = await axios.get(`${API}/data-health/run`);
      setAudit(res.data);
      const histRes = await axios.get(`${API}/data-health/history?limit=10`);
      setHistory(histRes.data);
    } catch (e) {
      console.error("Audit failed:", e);
    } finally {
      setRunning(false);
    }
  };

  const toggleExpand = (id) => setExpandedItems(prev => ({ ...prev, [id]: !prev[id] }));

  const formatDate = (iso) => {
    if (!iso) return "-";
    return new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  };

  const getScoreColor = (score) => {
    if (score >= 90) return "text-emerald-600";
    if (score >= 70) return "text-amber-600";
    return "text-red-600";
  };

  const getScoreBg = (score) => {
    if (score >= 90) return "from-emerald-500 to-emerald-600";
    if (score >= 70) return "from-amber-500 to-amber-600";
    return "from-red-500 to-red-600";
  };

  return (
    <Layout>
      <div className="p-4 md:p-8 max-w-6xl mx-auto" data-testid="data-health-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-6 gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <ShieldCheck className="w-6 h-6 text-emerald-600" /> Data Health
            </h1>
            <p className="text-sm text-slate-500 mt-1">Cross-module consistency audit &bull; Auto-runs daily</p>
          </div>
          <Button
            data-testid="run-audit-btn"
            onClick={runAudit}
            disabled={running}
            className="bg-slate-900 hover:bg-slate-800 text-white"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${running ? "animate-spin" : ""}`} />
            {running ? "Running Audit..." : "Run Audit Now"}
          </Button>
        </div>

        {loading ? (
          <div className="text-center py-12 text-slate-400">Loading audit data...</div>
        ) : !audit || !audit.health_score ? (
          <Card className="border-slate-200">
            <CardContent className="p-12 text-center">
              <Database className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-slate-700 mb-2">No Audit Run Yet</h3>
              <p className="text-sm text-slate-400 mb-4">Click "Run Audit Now" to perform your first data health check</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-6">
            {/* Staleness Warning */}
            {audit.run_at && (() => {
              const auditDate = new Date(audit.run_at);
              const now = new Date();
              const daysSinceAudit = Math.floor((now - auditDate) / (1000 * 60 * 60 * 24));
              if (daysSinceAudit >= 7) {
                return (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start gap-3" data-testid="staleness-warning">
                    <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-amber-800">
                        Audit data is {daysSinceAudit} days old
                      </p>
                      <p className="text-xs text-amber-600 mt-1">
                        The numbers shown may not reflect recent changes. Click "Run Audit Now" to get fresh data.
                      </p>
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={runAudit}
                      disabled={running}
                      className="border-amber-300 text-amber-700 hover:bg-amber-100"
                    >
                      <RefreshCw className={`w-3 h-3 mr-1 ${running ? "animate-spin" : ""}`} />
                      Refresh
                    </Button>
                  </div>
                );
              }
              return null;
            })()}
            {/* Score + Summary */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <Card className="md:col-span-1 border-slate-200" data-testid="health-score-card">
                <CardContent className="p-6 text-center">
                  <div className={`w-24 h-24 mx-auto rounded-full bg-gradient-to-br ${getScoreBg(audit.health_score)} flex items-center justify-center mb-3 shadow-lg`}>
                    <span className="text-3xl font-bold text-white">{audit.health_score}</span>
                  </div>
                  <p className="text-sm font-medium text-slate-600">Health Score</p>
                  <p className="text-xs text-slate-400 mt-1">Last run: {formatDate(audit.run_at)}</p>
                </CardContent>
              </Card>

              <div className="md:col-span-3 grid grid-cols-3 gap-3">
                <SeverityCard severity="high" count={audit.severity_breakdown?.high || 0} label="Critical Issues" />
                <SeverityCard severity="medium" count={audit.severity_breakdown?.medium || 0} label="Warnings" />
                <SeverityCard severity="low" count={audit.severity_breakdown?.low || 0} label="Minor" />
              </div>
            </div>

            {/* DB Stats */}
            {audit.stats && (
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-3" data-testid="db-stats-grid">
                <MiniStat icon={Users} label="Leads" value={audit.stats.total_leads} />
                <MiniStat icon={Users} label="Users" value={audit.stats.total_users} />
                <MiniStat icon={FileText} label="EOIs" value={audit.stats.total_eois} />
                <MiniStat icon={Activity} label="Visits" value={audit.stats.total_visits} />
                <MiniStat icon={Building2} label="Projects" value={audit.stats.total_projects} />
              </div>
            )}

            {/* Issues List */}
            {audit.issues?.length > 0 && (
              <div data-testid="issues-section">
                <h3 className="text-sm font-semibold text-slate-700 mb-3 uppercase tracking-wider">Issues ({audit.issues.length})</h3>
                <div className="space-y-2">
                  {audit.issues.map((issue, idx) => (
                    <IssueCard key={idx} item={issue} index={idx} expanded={expandedItems[`issue_${idx}`]} onToggle={() => toggleExpand(`issue_${idx}`)} />
                  ))}
                </div>
              </div>
            )}

            {/* Warnings List */}
            {audit.warnings?.length > 0 && (
              <div data-testid="warnings-section">
                <h3 className="text-sm font-semibold text-slate-700 mb-3 uppercase tracking-wider">Warnings ({audit.warnings.length})</h3>
                <div className="space-y-2">
                  {audit.warnings.map((w, idx) => (
                    <IssueCard key={idx} item={w} index={idx} expanded={expandedItems[`warn_${idx}`]} onToggle={() => toggleExpand(`warn_${idx}`)} />
                  ))}
                </div>
              </div>
            )}

            {audit.issues?.length === 0 && audit.warnings?.length === 0 && (
              <Card className="border-emerald-200 bg-emerald-50">
                <CardContent className="p-8 text-center">
                  <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto mb-3" />
                  <h3 className="text-lg font-medium text-emerald-800">All Clear</h3>
                  <p className="text-sm text-emerald-600">No data consistency issues found across all modules</p>
                </CardContent>
              </Card>
            )}

            {/* Cross-module check */}
            {audit.cross_module_check && (
              <Card className="border-slate-200" data-testid="cross-module-check">
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <Database className="w-4 h-4 text-slate-500" /> Cross-Module Counts
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div>
                      <p className="text-slate-400 text-xs">Total in DB</p>
                      <p className="font-bold text-slate-900">{audit.cross_module_check.total_leads_db}</p>
                    </div>
                    <div>
                      <p className="text-slate-400 text-xs">With Project</p>
                      <p className="font-bold text-slate-900">{audit.cross_module_check.total_leads_with_project}</p>
                    </div>
                    <div>
                      <p className="text-slate-400 text-xs">Orphan Leads</p>
                      <p className={`font-bold ${audit.cross_module_check.orphan_leads > 0 ? "text-amber-600" : "text-emerald-600"}`}>{audit.cross_module_check.orphan_leads}</p>
                    </div>
                    <div>
                      <p className="text-slate-400 text-xs">Aligned?</p>
                      <p className={`font-bold ${audit.cross_module_check.modules_aligned ? "text-emerald-600" : "text-amber-600"}`}>
                        {audit.cross_module_check.modules_aligned ? "Yes" : "No"}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Audit History */}
            {history.length > 1 && (
              <Card className="border-slate-200" data-testid="audit-history">
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <Clock className="w-4 h-4 text-slate-500" /> Audit History
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 border-y border-slate-200">
                      <tr>
                        <th className="text-left px-4 py-2 font-medium text-slate-500">Date</th>
                        <th className="text-left px-4 py-2 font-medium text-slate-500">Score</th>
                        <th className="text-left px-4 py-2 font-medium text-slate-500">Issues</th>
                        <th className="text-left px-4 py-2 font-medium text-slate-500">Warnings</th>
                        <th className="text-left px-4 py-2 font-medium text-slate-500">Breakdown</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {history.map(h => (
                        <tr key={h.audit_id} className="hover:bg-slate-50">
                          <td className="px-4 py-2.5 text-slate-600">{formatDate(h.run_at)}</td>
                          <td className="px-4 py-2.5">
                            <span className={`font-bold ${getScoreColor(h.health_score)}`}>{h.health_score}</span>
                          </td>
                          <td className="px-4 py-2.5 text-slate-600">{h.total_issues}</td>
                          <td className="px-4 py-2.5 text-slate-600">{h.total_warnings}</td>
                          <td className="px-4 py-2.5">
                            {h.severity_breakdown?.high > 0 && <Badge className="bg-red-100 text-red-700 mr-1 text-[10px]">{h.severity_breakdown.high}H</Badge>}
                            {h.severity_breakdown?.medium > 0 && <Badge className="bg-amber-100 text-amber-700 mr-1 text-[10px]">{h.severity_breakdown.medium}M</Badge>}
                            {h.severity_breakdown?.low > 0 && <Badge className="bg-blue-100 text-blue-700 text-[10px]">{h.severity_breakdown.low}L</Badge>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </CardContent>
              </Card>
            )}
          </div>
        )}
      </div>
    </Layout>
  );
}

function SeverityCard({ severity, count, label }) {
  const cfg = SEVERITY_CONFIG[severity];
  const Icon = cfg.icon;
  return (
    <Card className={`border ${count > 0 ? cfg.color : "border-slate-200 bg-slate-50 text-slate-400"}`} data-testid={`severity-${severity}`}>
      <CardContent className="p-4 flex items-center gap-3">
        <Icon className={`w-8 h-8 ${count > 0 ? cfg.iconColor : "text-slate-300"}`} />
        <div>
          <p className="text-2xl font-bold">{count}</p>
          <p className="text-xs">{label}</p>
        </div>
      </CardContent>
    </Card>
  );
}

function MiniStat({ icon: Icon, label, value }) {
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-3 flex items-center gap-2">
      <Icon className="w-4 h-4 text-slate-400" />
      <div>
        <p className="text-xs text-slate-400">{label}</p>
        <p className="text-sm font-bold text-slate-900">{value}</p>
      </div>
    </div>
  );
}

function IssueCard({ item, index, expanded, onToggle }) {
  const navigate = useNavigate();
  const cfg = SEVERITY_CONFIG[item.severity] || SEVERITY_CONFIG.low;
  const Icon = cfg.icon;

  const handleLinkClick = (e, path, item) => {
    e.stopPropagation();
    // If the issue has affected_lead_ids, store them for the leads page
    if (item?.affected_lead_ids?.length > 0 && path.startsWith("/leads")) {
      sessionStorage.setItem("dh_filter_ids", JSON.stringify(item.affected_lead_ids));
      sessionStorage.setItem("dh_filter_title", item.title);
      navigate("/leads?dh_filter=true");
    } else {
      navigate(path);
    }
  };

  const renderSample = (sample) => {
    if (!sample || sample.length === 0) return null;

    // Check if items have lead_id (clickable leads)
    const first = sample[0];
    if (typeof first === "object" && first.lead_id) {
      return (
        <div className="space-y-1">
          <p className="font-medium mb-1">Sample records:</p>
          {sample.map((s, i) => (
            <button
              key={i}
              onClick={(e) => handleLinkClick(e, `/leads/${s.lead_id}`)}
              className="flex items-center gap-2 text-left w-full hover:bg-white/60 rounded px-2 py-1 transition-colors group"
              data-testid={`sample-lead-${s.lead_id}`}
            >
              <ExternalLink className="w-3 h-3 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity" />
              <span className="font-medium">{s.name || s.lead_id}</span>
              {s.project_interest && <span className="opacity-60">({s.project_interest})</span>}
              {s.assigned_to && <span className="opacity-60">assigned: {s.assigned_to}</span>}
              {s.phone && <span className="opacity-60">{s.phone}</span>}
            </button>
          ))}
        </div>
      );
    }

    // Duplicate phone format: {phone, count, leads[]}
    if (typeof first === "object" && first.phone && first.leads) {
      return (
        <div className="space-y-2">
          <p className="font-medium mb-1">Duplicate numbers:</p>
          {sample.map((dup, i) => (
            <div key={i} className="bg-white/40 rounded px-2 py-1.5">
              <p className="font-mono font-medium">{dup.phone} <span className="opacity-60">({dup.count} leads)</span></p>
              <div className="ml-3 mt-1 space-y-0.5">
                {dup.leads.map((ld, j) => (
                  <button
                    key={j}
                    onClick={(e) => handleLinkClick(e, `/leads/${ld.lead_id}`)}
                    className="flex items-center gap-1.5 text-left hover:bg-white/60 rounded px-1 py-0.5 transition-colors group"
                    data-testid={`sample-dup-${ld.lead_id}`}
                  >
                    <ExternalLink className="w-3 h-3 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity" />
                    <span>{ld.name || ld.lead_id}</span>
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      );
    }

    // Fallback: raw JSON
    return (
      <div>
        <p className="font-medium mb-1">Sample records:</p>
        <pre className="bg-white/50 rounded p-2 overflow-x-auto text-[11px]">
          {JSON.stringify(sample, null, 2)}
        </pre>
      </div>
    );
  };

  return (
    <div className={`border rounded-lg ${cfg.color}`} data-testid={`issue-${index}`}>
      <button onClick={onToggle} className="w-full flex items-center gap-3 px-4 py-3 text-left">
        <Icon className={`w-5 h-5 shrink-0 ${cfg.iconColor}`} />
        <div className="flex-1 min-w-0">
          <p className="font-medium text-sm">{item.title}</p>
          <p className="text-xs opacity-80 truncate">{item.description}</p>
        </div>
        <Badge className={`${cfg.badge} text-[10px] shrink-0`}>{item.count}</Badge>
        {expanded ? <ChevronDown className="w-4 h-4 shrink-0" /> : <ChevronRight className="w-4 h-4 shrink-0" />}
      </button>
      {expanded && (
        <div className="px-4 pb-3 text-xs space-y-2 border-t border-current/10 pt-2">
          <p>{item.description}</p>
          {item.fix_suggestion && (
            <p className="font-medium">Suggested fix: {item.fix_suggestion}</p>
          )}
          {item.sample && renderSample(item.sample)}
          {item.link && (
            <button
              onClick={(e) => handleLinkClick(e, item.link, item)}
              className="inline-flex items-center gap-1.5 mt-1 px-3 py-1.5 bg-white/60 hover:bg-white rounded-md font-medium transition-colors"
              data-testid={`issue-link-${index}`}
            >
              <ExternalLink className="w-3 h-3" />
              {item.link_label || "View"}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
